import { FrameRange } from 'content-creation-common/models';

import { isNotNumber } from '../../../util/numbers';
import { isNil } from '../../../util/object';

import { CHAPTER_LENGTH_MIN_SECOND } from '../constants';
import { PLAYBACK_FRAMES_PER_SECOND } from '../../../constants';
import { isEmpty as isEmptyArray } from '../../../util/array';

// This error amount is given to any shot combination that is smaller than our smallest allowed chapter length.
// Use a large number here but not too large because we could have multiple of these added together and we don't want to overflow
const MAX_ERROR_AMT = 10000;

class MinPathNode {
  constructor(errAmtFromHereDown, thisRow, thisCol) {
    this.errAmtFromHereDown = errAmtFromHereDown;
    this.thisCol = thisCol;
    this.thisRow = thisRow;
  }
}

// This matrix contains sums going from the start position in the row and the end position in the column. Each position is the sum of all of
// the shots from the start position to the end position
function createLengthMatrix(shots) {
  const matrix = [];
  for (let i = 0; i < shots.length; i++) {
    matrix[i] = [];
    for (let j = i; j < shots.length; j++) {
      const previous = j > i ? matrix[i][j - 1] : 0;
      const d = shots[j];
      matrix[i][j] = d + previous;
    }
  }
  return matrix;
}

function populateRowsSummary(matrix, targetLength, longestAllowedChapter) {
  const bestNodeInEachRow = [];

  // Start at the bottom and work our way up
  for (let row = matrix.length - 1; row >= 0; row--) {
    let min = Number.MAX_SAFE_INTEGER;

    // Check the last row we just came from
    for (let col = matrix.length - 1; col >= row; col--) {
      // Find the error for this position.
      // If the sum at this position is less than our minimum allowed chapter length then set its error to some large number so we never
      // pick it
      let thisNodesErrorAmt;
      if (matrix[row][col] > longestAllowedChapter && row !== col) {
        // If this node represents a chapter of only one shot then allow it to be above the longestAllowedChapter
        thisNodesErrorAmt = MAX_ERROR_AMT;
      } else {
        thisNodesErrorAmt = Math.abs(targetLength - matrix[row][col]);
      }

      // We get this nodes parent by going to rows[col + 1]. this is because col represents the ending shot and so the
      // next shot would start at col+1
      const thisNodesParent = bestNodeInEachRow[col + 1];
      const thisNodesParentsErrorAmt = thisNodesParent
        ? thisNodesParent.errAmtFromHereDown
        : 0;
      const thisTotal = thisNodesErrorAmt + thisNodesParentsErrorAmt;

      if (thisTotal < min) {
        min = thisTotal;

        // This is our best node in this row so far
        // Update this row with this node, Using thisTotal And row/col
        bestNodeInEachRow[row] = new MinPathNode(thisTotal, row, col);
      }
    }
  }

  return bestNodeInEachRow;
}

function getBestPathFromRowSummary(rows, originalShotList) {
  if (rows.length <= 0) {
    return [];
  }

  const bestPath = [];
  let row = rows[0];
  // console.log("Total Error: " + row.errAmtFromHereDown); // useful for debugging
  while (row) {
    // Convert the shot indexes into frame numbers from the original shot list
    const startShotIndex = row.thisRow;
    const endShotIndex = row.thisCol;
    const startFrame = originalShotList[startShotIndex].start; // Get the first frame of the first shot
    const endFrame = originalShotList[endShotIndex].end; // Get the last frame of the ending shot
    bestPath.push({ start: startFrame, end: endFrame });

    // Get the next step in the path
    row = rows[endShotIndex + 1];
  }

  return bestPath;
}

/**
 * Given a list of shots return the shots grouped together into "chapters" such that each chapter is as close as possible to the target
 * length
 * requires shot to be sorted
 *
 * @param {Object[]} shots - The groupings of frames which make up a shot in a video
 * @param {Number} shots[].start - The start frame of the shot (Inclusive)
 * @param {Number} shots[].end - The end frame of the shot (Inclusive)
 * @param {Number} targetLength - The ideal length for each chapter in second
 * @param {Number} longestAllowedChapter - The longest allowable chapter size in second
 * @returns {Array|*} chapters - A list of start and end values like [{ start: Number, end: Number }, ... ]
 */
export function getChapterBreaks(shots, targetLength, longestAllowedChapter) {
  if (shots.length <= 0) {
    return [];
  }
  if (isNil(shots[0].start) || isNil(shots[0].end)) {
    return [];
  }
  if (targetLength <= 0) {
    return [];
  }

  const targetLengthInFrame = targetLength * PLAYBACK_FRAMES_PER_SECOND;
  const maxLengthInFrame = longestAllowedChapter * PLAYBACK_FRAMES_PER_SECOND;

  // Were interested in the lengths because we want to optimize the length of each chapter so convert the shots to lengths
  const shotLengths = shots.map((shot) => shot.end - shot.start + 1);

  // Next create a matrix of the length of each shot grouping starting a shot A ending at shot B where A < B for all As and Bs
  const matrix = createLengthMatrix(shotLengths);

  // For each starting shot find the best possible shot to end on. Where best is defined as the shot which is part of a path with the
  // least amount of difference from the target for each chapter
  const rows = populateRowsSummary(
    matrix,
    targetLengthInFrame,
    maxLengthInFrame,
  );

  // Once we have calculated the best ending shot for each shot, start at shot 0 and follow the children to build the list of chapters
  return getBestPathFromRowSummary(rows, shots);
}

/**
 * validate whether the new duration meets the min requirement for chapter break placement
 *
 * @param {number} currentFrameCount - current duration of a chapter duration in seconds
 * @param {number} toRemoveFrameCount
 * @param {number} minChapterLength
 */
export function isValidChapterBreakPlacement(
  currentFrameCount,
  toRemoveFrameCount,
  minChapterLength = CHAPTER_LENGTH_MIN_SECOND,
) {
  if (isNotNumber(currentFrameCount) || isNotNumber(toRemoveFrameCount)) {
    return false;
  }
  return (
    currentFrameCount - toRemoveFrameCount >=
    minChapterLength * PLAYBACK_FRAMES_PER_SECOND
  );
}

/**
 * Get an array of shots in the given FrameRange.
 *
 * @param {FrameRange} frameRange
 * @param {array} allShots
 * @returns {array} of shots in given frame range
 */
export const getShotsForFrameRange = (frameRange, allShots) => {
  if (!FrameRange.isFrameRange(frameRange) || isEmptyArray(allShots)) return [];
  const shotsInThisFrameRange = [];
  allShots.forEach((shot) => {
    // Get FrameRange for this shot
    const { endFrame, startFrame } = shot;
    if (isNil(endFrame) || isNil(startFrame)) return;
    const shotFrameRange = new FrameRange(startFrame, endFrame);
    if (frameRange.contains(shotFrameRange)) {
      shotsInThisFrameRange.push(shot);
    }
  });
  return shotsInThisFrameRange;
};
